import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { cx } from '../lib/cx';

export type ToastTone = 'neutral' | 'success' | 'danger';

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  toast: (message: string, tone?: ToastTone) => void;
  toasts: readonly ToastItem[];
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE: Record<ToastTone, string> = {
  neutral: 'bg-inverse text-ink-ondark',
  success: 'bg-success text-surface',
  danger: 'bg-danger text-surface',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const next = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, tone: ToastTone = 'neutral') => {
      const id = next.current++;
      setToasts((list) => [...list, { id, message, tone }]);
      setTimeout(() => dismiss(id), 4000);
    },
    [dismiss],
  );

  const value = useMemo<ToastApi>(() => ({ toast, toasts, dismiss }), [toast, toasts, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 md:bottom-6"
      >
        {toasts.map((t) => (
          <ToastBubble key={t.id} item={t} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Poruka greške mora prekinuti čitač zaslona, potvrda spremanja ne smije.
 * Okvir je `polite`, a pojedinačni `danger` mjehurić nosi `alert` — sve u
 * jednom području, pa se poruke i dalje slažu jedna ispod druge.
 */
function ToastBubble({ item }: { item: ToastItem }) {
  return (
    <div
      {...(item.tone === 'danger' ? { role: 'alert' as const } : {})}
      className={cx(
        'pointer-events-auto max-w-narrow rounded-pill px-5 py-3 text-[14px] shadow-overlay',
        TONE[item.tone],
      )}
    >
      {item.message}
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast mora biti unutar <ToastProvider>');
  return ctx;
}
